import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { getCampaignLaunchReadinessQueryKey } from 'services/campaigns'
import { baseAPICall } from 'services/baseAPICall'
import { clientInstance } from 'utils/http-client'

// Types
export interface Lead {
  id: number
  email: string
  full_name: string
  first_name: string
  last_name: string
  title?: string | null
  company?: string | null
  custom_variables?: Array<{ name: string; value: string }>
  verification_status: string
  emails_sent: number
  opens: number
  unique_opens: number
  replies: number
  unique_replies: number
}

export interface PaginatedLeadsResponse {
  count: number
  next: string | null
  previous: string | null
  results: Lead[]
}

export interface SyncLeadsResponse {
  data: Lead[]
  message: string
}

// Query keys
export const leadKeys = {
  all: ['leads'] as const,
  byBisonCampaignBase: (bisonCampaignId: number) =>
    [...leadKeys.all, 'bison_campaign', bisonCampaignId] as const,
  byBisonCampaign: (bisonCampaignId: number, page: number) =>
    [...leadKeys.byBisonCampaignBase(bisonCampaignId), 'page', page] as const,
}

// API calls
const getCampaignLeads = async (
  bisonCampaignId: number,
  page: number = 1
): Promise<PaginatedLeadsResponse> => {
  const response = await baseAPICall<PaginatedLeadsResponse>(() =>
    clientInstance.get(`/emailbison/campaigns/${bisonCampaignId}/leads/`, {
      params: { page },
    })
  )
  return response
}

const syncCampaignLeads = async (
  bisonCampaignId: number
): Promise<SyncLeadsResponse> => {
  return baseAPICall<SyncLeadsResponse>(() =>
    clientInstance.post(`/emailbison/campaigns/${bisonCampaignId}/leads/sync/`)
  )
}

// Hooks
export const useGetCampaignLeads = (
  bisonCampaignId: number,
  page: number = 1,
  options?: { enabled?: boolean }
) => {
  return useQuery({
    queryKey: leadKeys.byBisonCampaign(bisonCampaignId, page),
    queryFn: () => getCampaignLeads(bisonCampaignId, page),
    enabled: bisonCampaignId > 0 && (options?.enabled ?? true),
  })
}

export const useSyncCampaignLeads = (
  bisonCampaignId: number,
  campaignId?: number
) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: () => syncCampaignLeads(bisonCampaignId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: leadKeys.byBisonCampaignBase(bisonCampaignId),
      })
      // Invalidate lead review cache so bison_lead_id is refreshed
      if (campaignId) {
        queryClient.invalidateQueries({
          queryKey: ['campaign-lead-review', campaignId],
        })
      }
      // Invalidate campaigns list + readiness so counts are fresh
      queryClient.invalidateQueries({
        queryKey: ['ALL_CAMPAIGNS_TABLE'],
        exact: false,
      })
      if (campaignId) {
        queryClient.invalidateQueries({
          queryKey: getCampaignLaunchReadinessQueryKey({
            campaign_id: campaignId,
          }),
        })
      }
    },
  })
}
